import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../i18n';
import { getBadgeRules } from '../services/api';

const BadgeRulesPage = () => {
  const { t, i18n } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  const [badgeRules, setBadgeRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedBadges, setExpandedBadges] = useState(new Set());

  useEffect(() => {
    fetchBadgeRules();
  }, []);

  const fetchBadgeRules = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getBadgeRules();
      setBadgeRules(response.results || response);
    } catch (err) {
      console.error('Failed to fetch badge rules:', err);
      setError(t('badgeRules.loadFailedRetry'));
    } finally {
      setLoading(false);
    }
  };

  const toggleBadgeExpansion = (badgeId) => {
    const newExpanded = new Set(expandedBadges);
    if (newExpanded.has(badgeId)) {
      newExpanded.delete(badgeId);
    } else {
      newExpanded.add(badgeId);
    }
    setExpandedBadges(newExpanded);
  };

  const formatDate = (dateString) => {
    if (!dateString) return tCommon('na');
    const date = new Date(dateString);
    return date.toLocaleDateString(getDateLocale(i18n.language), {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  /** Format decimal hours as HH:MM for Minimum Hours display. */
  const formatMinHoursHHMM = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const num = parseFloat(value);
    if (isNaN(num) || num < 0) return String(value);
    if (num === 0) return '0:00';
    const h = Math.floor(num);
    const m = Math.round((num % 1) * 60);
    return `${h}:${m.toString().padStart(2, '0')}`;
  };

  const getBadgeTypeColor = (badgeType) => {
    switch (badgeType) {
      case 'DAILY':
        return 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-700/70';
      case 'WEEKLY':
        return 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-700/70';
      case 'MONTHLY':
        return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-700/70';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80';
    }
  };

  const getTieHandlingDescription = (tieHandling) => {
    switch (tieHandling) {
      case 'no_badge':
        return t('badgeRules.tieNoBadge');
      case 'all_awarded':
        return t('badgeRules.tieAllAwarded');
      case 'random':
        return t('badgeRules.tieRandom');
      default:
        return tieHandling;
    }
  };

  const labelForKey = (k) => (k || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

  /** Format a single value for inline display (old -> new). */
  const formatInline = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'object') {
      if (Array.isArray(value)) return value.length ? value.map((v) => (typeof v === 'object' ? JSON.stringify(v) : String(v))).join(', ') : '—';
      return Object.keys(value).length ? JSON.stringify(value) : '—';
    }
    return String(value);
  };

  /** Get only changed key/value pairs between old and new (for old -> new view). */
  const getChangedPairs = (oldVal, newVal) => {
    const pairs = [];
    const oldObj = oldVal != null && typeof oldVal === 'object' && !Array.isArray(oldVal);
    const newObj = newVal != null && typeof newVal === 'object' && !Array.isArray(newVal);
    if (oldObj && newObj) {
      const keys = new Set([...Object.keys(oldVal), ...Object.keys(newVal)]);
      keys.forEach((k) => {
        const o = oldVal[k];
        const n = newVal[k];
        if (JSON.stringify(o) !== JSON.stringify(n)) pairs.push({ key: k, oldV: o, newV: n });
      });
    } else if (newObj && (oldVal == null || !oldObj)) {
      Object.entries(newVal).forEach(([k, v]) => pairs.push({ key: k, oldV: undefined, newV: v }));
    } else if (oldObj && (newVal == null || !newObj)) {
      Object.entries(oldVal).forEach(([k, v]) => pairs.push({ key: k, oldV: v, newV: undefined }));
    } else {
      pairs.push({ key: null, oldV: oldVal, newV: newVal });
    }
    return pairs;
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg p-8 max-w-md border border-slate-200 dark:border-slate-600">
          <div className="text-center">
            <span className="material-symbols-outlined text-red-500 dark:text-red-400 text-5xl mb-4">error</span>
            <h2 className="text-xl font-bold text-slate-900 dark:text-slate-100 mb-2">{t('badgeRules.errorLoading')}</h2>
            <p className="text-slate-600 dark:text-slate-400 mb-4">{error}</p>
            <button
              onClick={fetchBadgeRules}
              className="px-4 py-2 bg-blue-600 dark:bg-primary text-white rounded-lg hover:bg-blue-700 dark:hover:bg-primary/90 transition-colors"
            >
              {tCommon('tryAgain')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-600">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                <span className="material-symbols-outlined text-blue-600 dark:text-blue-400 text-3xl">rule</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold text-slate-900 dark:text-slate-100">{t('badgeRules.calculationTitle')}</h1>
                <p className="text-slate-600 dark:text-slate-400 mt-1">
                  {t('badgeRules.subtitle')}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Badge Rules List */}
        {badgeRules.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-white dark:bg-slate-800 rounded-xl shadow-sm p-8 border border-slate-200 dark:border-slate-600 text-center"
          >
            <span className="material-symbols-outlined text-slate-400 dark:text-slate-500 text-5xl mb-4">info</span>
            <p className="text-slate-600 dark:text-slate-400">{t('badgeRules.noRulesAvailable')}</p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {badgeRules.map((badge, index) => (
              <motion.div
                key={badge.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-600 overflow-hidden"
              >
                {/* Badge Header */}
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h2 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{badge.name}</h2>
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getBadgeTypeColor(badge.badge_type)}`}>
                          {badge.badge_type_display || badge.badge_type || 'N/A'}
                        </span>
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200 dark:bg-slate-700/60 dark:text-slate-200 dark:border-slate-600/80">
                          {tCommon('versionNumber', { version: badge.version })}
                        </span>
                      </div>
                      {badge.description && (
                        <p className="text-slate-600 dark:text-slate-400 mb-4">{badge.description}</p>
                      )}
                      
                      {/* Rule Details */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('badgeRules.pointsValue')}</div>
                          <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">{badge.points_value}</div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('badgeRules.effectiveDateLabel')}</div>
                          <div className="text-sm text-slate-900 dark:text-slate-100">{formatDate(badge.effective_date)}</div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('badgeRules.minOperations')}</div>
                          <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{badge.min_operations || 0}</div>
                        </div>
                        {badge.min_hours != null && badge.min_hours !== '' && (
                          <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                            <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('badgeRules.minHours')}</div>
                            <div className="text-lg font-semibold text-slate-900 dark:text-slate-100">{formatMinHoursHHMM(badge.min_hours)}</div>
                          </div>
                        )}
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('badgeRules.tieHandling')}</div>
                          <div className="text-sm text-slate-900 dark:text-slate-100">{getTieHandlingDescription(badge.tie_handling)}</div>
                        </div>
                        <div className="bg-slate-50 dark:bg-slate-700/50 rounded-lg p-4">
                          <div className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{t('badgeRules.applicableTo')}</div>
                          <div className="text-sm text-slate-900 dark:text-slate-100">
                            {badge.is_global ? (
                              <span className="font-medium text-green-600 dark:text-green-400">{t('badgeRules.allTeams')}</span>
                            ) : (
                              <div className="mt-1">
                                {badge.applicable_teams && badge.applicable_teams.length > 0 ? (
                                  badge.applicable_teams.map((team, idx) => (
                                    <span key={team.id} className="inline-block mr-2">
                                      {team.name}{idx < badge.applicable_teams.length - 1 ? ',' : ''}
                                    </span>
                                  ))
                                ) : (
                                  <span className="text-slate-500 dark:text-slate-400">{t('badgeRules.noTeamsSpecified')}</span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Historical Versions Toggle */}
                  {badge.historical_versions && badge.historical_versions.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-200 dark:border-slate-600">
                    <button
                      onClick={() => toggleBadgeExpansion(badge.id)}
                      className="flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium transition-colors"
                    >
                      <span className="material-symbols-outlined text-lg">
                        {expandedBadges.has(badge.id) ? 'expand_less' : 'expand_more'}
                      </span>
                      <span>
                        {expandedBadges.has(badge.id) ? t('badgeRules.hideHistorical', { count: badge.historical_versions.length }) : t('badgeRules.showHistorical', { count: badge.historical_versions.length })}
                      </span>
                    </button>
                  </div>
                  )}
                </div>

                {/* Historical Versions */}
                <AnimatePresence>
                  {expandedBadges.has(badge.id) && badge.historical_versions && badge.historical_versions.length > 0 && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/30"
                    >
                      <div className="p-6">
                        <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-4">{t('badgeRules.historicalVersions')}</h3>
                        <div className="space-y-4">
                          {badge.historical_versions.map((history, idx) => (
                            <div
                              key={history.id}
                              className="bg-white dark:bg-slate-800 rounded-lg p-4 border border-slate-200 dark:border-slate-600"
                            >
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                                    {history.get_change_type_display || history.change_type}
                                  </span>
                                  {history.field_name && (
                                    <span className="text-xs text-slate-500 dark:text-slate-400">({history.field_name})</span>
                                  )}
                                </div>
                                <span className="text-xs text-slate-500 dark:text-slate-400">
                                  {formatDate(history.changed_at)}
                                </span>
                              </div>
                              {history.changed_by_name && (
                                <div className="text-xs text-slate-600 dark:text-slate-400 mb-2">
                                  {t('badgeRules.changedBy', { name: history.changed_by_name })}
                                </div>
                              )}
                              {history.change_type !== 'CREATED' && (history.old_value_display != null || history.new_value_display != null) && (() => {
                                const pairs = getChangedPairs(history.old_value_display, history.new_value_display);
                                if (pairs.length === 0) return null;
                                return (
                                  <div className="space-y-2 mt-3">
                                    {pairs.map(({ key, oldV, newV }) => {
                                      const isMinHours = key === 'min_hours' || history.field_name === 'min_hours';
                                      const fmt = (v) => (isMinHours ? (formatMinHoursHHMM(v) || formatInline(v)) : formatInline(v));
                                      return (
                                      <div key={key ?? 'value'} className="text-sm flex flex-wrap items-baseline gap-1">
                                        <span className="font-medium text-slate-700 dark:text-slate-300">
                                          {(key != null ? labelForKey(key) : (history.field_name ? labelForKey(history.field_name) : t('badgeRules.value')))}:
                                        </span>
                                        <span className="text-slate-600 dark:text-slate-400 bg-slate-50 dark:bg-slate-700 px-1.5 py-0.5 rounded">{fmt(oldV)}</span>
                                        <span className="text-slate-400 dark:text-slate-500">→</span>
                                        <span className="text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 px-1.5 py-0.5 rounded">{fmt(newV)}</span>
                                      </div>
                                    ); })}
                                  </div>
                                );
                              })()}
                              {history.change_reason && (
                                <div className="mt-2 text-xs text-slate-600 dark:text-slate-400 italic">
                                  {t('badgeRules.reason', { reason: history.change_reason })}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default BadgeRulesPage;

